import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'

import { initPushNotificationHandlers, type ApprovalActionId } from '@/platform/pushRoute'
import { followNotificationRoute, resolveNotificationRoute } from '@/lib/notificationRoute'
import { approveAction, rejectAction } from '@/api/agent'

/**
 * Route a tapped push notification into the SPA, and refresh live data when
 * a push arrives or the app returns to the foreground (#2567). Must be
 * mounted inside the Router — same shape as useOAuthReturn. No-op on web.
 */
export function usePushNotificationRouting(): void {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  useEffect(() => {
    let dispose: (() => void) | undefined
    let cancelled = false

    // Today's "Needs you" cards and the approvals badge are what a
    // backgrounded app is most likely to have missed (#2562's dispatcher runs
    // while the SSE connection may be suspended).
    const refresh = () => {
      queryClient.invalidateQueries({ queryKey: ['today'] })
      queryClient.invalidateQueries({ queryKey: ['agent-approvals'] })
    }

    // An in-notification approve/reject button (#2573): resolve it directly,
    // without navigating anywhere, so the OS never has to bring the app to
    // the foreground. If it can't complete (expired, already resolved, no
    // network), fall back to opening the card so the user can act by hand.
    const handleApprovalAction = async (action: ApprovalActionId, approvalId: string) => {
      try {
        if (action === 'approve') {
          await approveAction(approvalId)
        } else {
          await rejectAction(approvalId, 'Rejected from notification')
        }
        refresh()
      } catch {
        followNotificationRoute(
          resolveNotificationRoute({
            type: 'agent_approval_pending',
            entity_type: 'agent_approval',
            entity_id: approvalId,
          }),
          (to) => navigate(to)
        )
      }
    }

    void initPushNotificationHandlers({
      onTap: (route) => followNotificationRoute(route, (to) => navigate(to)),
      onApprovalAction: (action, approvalId) => void handleApprovalAction(action, approvalId),
      onForegroundReceive: refresh,
      onAppForeground: refresh,
    }).then((remove) => {
      if (cancelled) remove()
      else dispose = remove
    })

    return () => {
      cancelled = true
      dispose?.()
    }
  }, [navigate, queryClient])
}
